{
  const AB_CLASS = 'ab-fuel-bundle-message-v1';
  const LINE_PROPERTY = '_pdp_bundle';
  const SPECIAL_OFFER_GIFT_PROPERTY = '_special_offer_gift';
  const SYNC_SOURCE = 'fuel-bundle-cart-retag';
  let syncing = false;
  let syncQueued = false;

  const isAbActive = () => document.documentElement.classList.contains(AB_CLASS);

  const fetchCart = async () => {
    const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  };

  const isExcludedLine = (item) =>
    item.gift_card || item.final_line_price === 0 || Boolean(item.properties?.[SPECIAL_OFFER_GIFT_PROPERTY]);

  const isTaggedLine = (item) => item.properties?.[LINE_PROPERTY] === 'true';

  const findLineToReconcile = (cart) =>
    cart.items.find((item) => {
      const isTagged = isTaggedLine(item);

      if (!isAbActive()) return isTagged;

      return isExcludedLine(item) ? isTagged : !isTagged;
    });

  const reconcileLine = async (line) => {
    const properties = { ...(line.properties || {}) };

    if (isAbActive() && !isExcludedLine(line)) {
      properties[LINE_PROPERTY] = 'true';
    } else {
      delete properties[LINE_PROPERTY];
    }

    const response = await fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: line.key,
        quantity: line.quantity,
        properties,
      }),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Failed to reconcile cart line');
    return data;
  };

  const reloadCartDrawer = async (cart) => {
    const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to reload cart drawer');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const source = doc.querySelector('#CartDrawer .drawer__inner');
    const target = document.querySelector('#CartDrawer .drawer__inner');
    const cartDrawer = document.querySelector('cart-drawer');

    if (source && target) target.innerHTML = source.innerHTML;
    if (cartDrawer) cartDrawer.classList.toggle('is-empty', cart.item_count === 0);

    document.dispatchEvent(new CustomEvent('cart-upsell-slider:init'));
    document.dispatchEvent(new CustomEvent('handleSwatchHover:init'));
    document.dispatchEvent(new CustomEvent('variant-options:init'));
    document.dispatchEvent(new CustomEvent('cart-drawer:rendered', { detail: { source: SYNC_SOURCE } }));
  };

  const reloadMainCart = async () => {
    const mainCartItems = document.querySelector('cart-items');
    if (mainCartItems?.onCartUpdate) await mainCartItems.onCartUpdate();
  };

  const syncCartLines = async () => {
    if (syncing) {
      syncQueued = true;
      return;
    }

    syncing = true;

    try {
      await window.VbysCartRetag.withSwishCartSaveSuppressed(async () => {
        let cart = await fetchCart();
        let remainingChanges = cart.items.length;
        let changed = false;

        while (remainingChanges > 0) {
          const line = findLineToReconcile(cart);
          if (!line) break;

          cart = await reconcileLine(line);
          remainingChanges -= 1;
          changed = true;
        }

        const reconciliationPending = Boolean(findLineToReconcile(cart));
        if (reconciliationPending) syncQueued = true;

        if (changed && !reconciliationPending) {
          await Promise.all([reloadCartDrawer(cart), reloadMainCart()]);
        }
      });
    } catch (error) {
      console.error('fuel-bundle-cart-retag:', error);
    } finally {
      syncing = false;

      if (syncQueued) {
        syncQueued = false;
        syncCartLines();
      }
    }
  };

  const initCartRetag = () => {
    const handleCartRendered = (event) => {
      if (event.detail?.source === SYNC_SOURCE) return;
      syncCartLines();
    };

    const unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === SYNC_SOURCE) return;
      syncCartLines();
    });

    document.addEventListener('cart-drawer:rendered', handleCartRendered);

    const root = document.documentElement;
    let wasActive = isAbActive();

    const observer = new MutationObserver(() => {
      const isActive = isAbActive();
      if (isActive !== wasActive) syncCartLines();
      wasActive = isActive;
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    window.addEventListener(
      'pagehide',
      () => {
        observer.disconnect();
        unsubscribeCartUpdate();
        document.removeEventListener('cart-drawer:rendered', handleCartRendered);
      },
      { once: true },
    );

    syncCartLines();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartRetag);
  } else {
    initCartRetag();
  }
}
